import { Phone, MessageCircle, Clock, Shield, MapPin, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { useSettings } from "@/lib/hooks";
import { trackEvent } from "@/lib/track";

export default function HeroSection() {
  const s = useSettings();
  return (
    <section className="relative bg-gradient-to-br from-ink-950 via-ink-900 to-ink-800 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1686966933735-305bd8fe0a77?crop=entropy&cs=srgb&fm=jpg&q=80&w=1800')] bg-cover bg-center opacity-15" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink-950/95 via-ink-900/85 to-ink-900/55" />
      {/* Orange brand accent glow */}
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand/20 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-4 lg:px-6 py-16 sm:py-20 lg:py-28">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5 mb-6">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-white/90 text-sm font-semibold">Disponible maintenant — 24h/24</span>
          </div>

          <h1 className="text-white leading-tight" data-testid="hero-title">
            <span className="block text-4xl sm:text-5xl lg:text-6xl font-extrabold">Dépannage &</span>
            <span className="block text-4xl sm:text-5xl lg:text-6xl font-extrabold">Remorquage</span>
            <span className="block text-2xl sm:text-3xl lg:text-4xl font-bold text-white/70 mt-2">en Île-de-France</span>
          </h1>

          <p className="text-white/75 mt-5 text-base sm:text-lg max-w-lg leading-relaxed">
            {s?.hero_subtitle || "Intervention rapide pour panne, accident, batterie, crevaison ou transport de véhicule. Professionnels expérimentés, tarifs transparents."}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href={`tel:${s?.company_phone_raw || ""}`}
              onClick={() => trackEvent("click_call", "hero")}
              className="inline-flex items-center justify-center gap-3 bg-white text-brand-800 hover:bg-brand-50 font-bold text-base rounded-xl px-6 py-4 transition-all shadow-float"
              data-testid="hero-call-btn"
            >
              <Phone size={20} />
              <div className="text-left leading-tight">
                <div className="text-base font-extrabold">{s?.company_phone || "07 53 95 58 90"}</div>
                <div className="text-xs text-brand-600 font-semibold">Appel gratuit — Réponse immédiate</div>
              </div>
            </a>
            <a
              href={`https://wa.me/${s?.company_whatsapp || ""}`}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackEvent("click_whatsapp", "hero")}
              className="inline-flex items-center justify-center gap-3 bg-whats hover:bg-whats-hover text-white font-bold text-base rounded-xl px-6 py-4 transition-all"
              data-testid="hero-whatsapp-btn"
            >
              <MessageCircle size={20} />
              <span>WhatsApp</span>
            </a>
          </div>

          <Link
            to="/simulateur"
            onClick={() => trackEvent("click_devis", "hero_link", { label: "hero-quote-link" })}
            className="mt-5 inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-semibold transition-colors"
            data-testid="hero-quote-link"
          >
            Estimer mon prix en ligne &rarr;
          </Link>
        </div>

        <div className="mt-10 lg:mt-14 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { icon: Clock, label: "30-45 min", sub: "d'intervention" },
            { icon: Shield, label: "Assurés", sub: "et certifiés" },
            { icon: MapPin, label: "Toute l'IDF", sub: "75 à 95" },
            { icon: Star, label: "4.8/5", sub: "avis clients" },
          ].map(({ icon: Icon, label, sub }) => (
            <div key={label} className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 flex items-center gap-3">
              <Icon size={20} className="text-white/80 shrink-0" />
              <div>
                <div className="text-white font-bold text-sm">{label}</div>
                <div className="text-white/60 text-xs">{sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
